import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, CheckCircle, Users, Trophy, Target,
  Focus, SplitSquareVertical, Brain, Hand,
  Database, Eye, Zap, Lightbulb, AlertCircle, Clock, Gauge, Layers, Radio, BookOpen,
  Award, BarChart3, GraduationCap, HelpCircle, FileSearch, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { PageLayout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { apiClient } from '../lib/api';
import { EasterSaleBanner } from '../components/EasterSaleBanner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const faqData = [
  {
    question: "What are train driver psychometric tests?",
    answer: "Train driver psychometric tests are standardised assessments used by UK Train Operating Companies (TOCs) to evaluate whether candidates possess the cognitive abilities required to safely operate trains. These tests measure attention, concentration, reaction time, memory, spatial reasoning, and decision-making skills. They are aligned to the RIS-3751-TOM industry standard and form a critical part of the train driver selection process."
  },
  {
    question: "How hard are the train driver psychometric tests?",
    answer: "Train driver psychometric tests are considered challenging, with industry pass rates typically between 20-40% for first-time candidates. The tests require sustained concentration under time pressure, quick and accurate responses, and the ability to process multiple streams of information simultaneously. Many candidates underestimate the difficulty and fail due to lack of preparation."
  },
  {
    question: "How many attempts do you get at the psychometric tests?",
    answer: "Most Train Operating Companies allow candidates to retake psychometric tests after a waiting period, typically 6-12 months. However, some TOCs limit the total number of attempts, often 2-3 maximum. Failing multiple times can result in permanent exclusion from that company's recruitment process, making first-time success crucial."
  },
  {
    question: "What types of tests are included in train driver assessments?",
    answer: "UK train driver psychometric assessments typically include concentration tests, attention tests, vigilance and monitoring tasks, reaction time measurements, memory and recall exercises, spatial awareness and reasoning, perception and visual processing, fault diagnosis scenarios, and hand-eye coordination tasks. Our platform covers all assessment categories."
  },
  {
    question: "Can I prepare for train driver psychometric tests?",
    answer: "Yes, preparation can improve performance. Familiarity with test formats, time management strategies, and understanding what each test measures all contribute to better performance. TDF Psychometrics provides structured practice across all assessment areas."
  },
  {
    question: "Are these the actual tests used by train companies?",
    answer: "Our practice tests are designed to mirror the types of cognitive assessments used in UK train driver recruitment. While we cannot replicate proprietary tests, our exercises are aligned to RIS-3751-TOM standards and developed by rail industry professionals with direct experience of the assessment process. This ensures relevant, realistic preparation."
  },
  {
    question: "How long should I prepare before my assessment?",
    answer: "We recommend a minimum of 2-4 weeks of regular practice before your scheduled assessment. Ideally, complete at least 20-30 practice sessions across different test types. Focus on your weaker areas identified through progress tracking. Consistent daily practice of 30-60 minutes is more effective than cramming."
  },
  {
    question: "What happens if I fail the psychometric tests?",
    answer: "Failing train driver psychometric tests means you cannot proceed in the recruitment process with that TOC. You will typically need to wait before reapplying. This is why thorough preparation is important before attending a formal assessment."
  }
];

const testCategories = [
  {
    name: "Concentration Tests",
    description: "Assess your ability to maintain focus over extended periods, similar to Group Bourdon-style assessments.",
    icon: Focus
  },
  {
    name: "Attention - Selective & Divided",
    description: "Measure how well you focus on relevant information while filtering distractions, and handle multiple information streams.",
    icon: SplitSquareVertical
  },
  {
    name: "Vigilance & Monitoring",
    description: "Test sustained attention and ability to detect changes or anomalies over time - critical for signal monitoring.",
    icon: Eye
  },
  {
    name: "Reaction Time",
    description: "Evaluate speed and accuracy of responses to visual and auditory stimuli under time pressure.",
    icon: Zap
  },
  {
    name: "Memory & Recall",
    description: "Assess short-term and working memory capacity essential for following procedures and instructions.",
    icon: Brain
  },
  {
    name: "Spatial Reasoning",
    description: "Test ability to visualise and manipulate objects mentally - important for understanding track layouts.",
    icon: Layers
  },
  {
    name: "Perception & Visual Processing",
    description: "Measure speed and accuracy of visual information processing and pattern recognition.",
    icon: Eye
  },
  {
    name: "Fault Diagnosis",
    description: "Evaluate logical reasoning and problem-solving abilities in technical scenarios.",
    icon: AlertCircle
  }
];

export const HomePage = () => {
  const [tests, setTests] = useState([]);

  useEffect(() => {
    apiClient.getTests().then(res => setTests(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const existingScript = document.getElementById('faq-schema');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.id = 'faq-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('faq-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  return (
    <PageLayout>
      <SEO 
        title="Train Driver Psychometric Tests UK | Practice & Improve"
        description="Practice all UK train driver psychometric tests. Aligned to RIS-3751-TOM standard. Improve your chances of passing first time."
        canonical="/"
      />
      
      <EasterSaleBanner variant="full" />

      {/* New Test Live Banner for VSE */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-[#2CB67D] via-[#22a06b] to-[#2CB67D] py-2.5 px-4"
        data-testid="vse-live-banner"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-white">
          <span className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span className="font-semibold text-sm sm:text-base">NEW TEST LIVE</span>
          </span>
          <span className="text-sm sm:text-base opacity-95">
            Visual Search Exercise (VSE)
          </span>
        </div>
      </motion.div>
      
      <section className="relative min-h-[600px] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/psychometric-hero.jpg')` }}
        />
        <div className="hero-overlay absolute inset-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <Badge className="mb-4 bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white">
              RIS-3751-TOM Aligned | Built by Rail Industry Experts
            </Badge>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
              Train Driver Psychometric Tests – Practice & Improve (UK)
            </h1>
            
            <p className="text-lg text-white/90 mb-4 font-medium">
              Build the skills needed to prepare you for OPC, TOC or private train driver assessments.
              Practice all cognitive assessment areas used in UK rail recruitment,
              identify your weak areas, and build the confidence to pass first time.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-8 text-white/80 text-sm">
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-[#2CB67D]" />
                OPC & TOC assessment preparation
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-[#2CB67D]" />
                1,000+ candidates prepared
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-[#2CB67D]" />
                78% pass rate
              </span>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/tests">
                <Button 
                  size="lg" 
                  className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white btn-press"
                  data-testid="hero-start-btn"
                >
                  Start Free Practice Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>

              <Link to="/pricing">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10"
                  data-testid="hero-pricing-btn"
                >
                  View Pricing Plans
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {[
              { icon: Target, value: 'All', label: 'Assessment Areas' },
              { icon: Users, value: '1,000+', label: 'Candidates Prepared' },
              { icon: Trophy, value: '78%', label: 'Pass Rate' },
              { icon: GraduationCap, value: '5', label: 'Difficulty Levels' },
            ].map((stat, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-[#0B1D51]/5 rounded-sm flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-[#0B1D51]" />
                </div>
                <p className="font-heading text-3xl font-bold text-[#0B1D51]">{stat.value}</p>
                <p className="text-sm text-[#64748B]">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="mb-4 bg-[#0B1D51]/10 text-[#0B1D51]">
              Understanding the Assessment
            </Badge>
            
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] tracking-tight mb-6">
              What Are Train Driver Psychometric Tests?
            </h2>
            
            <div className="prose prose-lg max-w-none text-[#334155]">
              <p className="text-lg leading-relaxed mb-4">
                <strong>Train driver psychometric tests</strong> are standardised cognitive assessments used by UK Train Operating Companies (TOCs) to evaluate whether candidates possess the mental abilities required to safely operate passenger and freight trains. These tests are a mandatory part of the train driver selection process and are aligned to the <strong>RIS-3751-TOM</strong> industry standard.
              </p>
              
              <p className="leading-relaxed mb-4">
                The assessments measure critical cognitive functions including sustained attention, concentration under pressure, reaction time, memory recall, spatial reasoning, and decision-making ability. Train drivers must process multiple streams of information simultaneously while maintaining safety-critical awareness – skills that these tests are specifically designed to evaluate.
              </p>
              
              <p className="leading-relaxed">
                Unlike academic exams, psychometric tests assess underlying cognitive abilities and how you process information. However, familiarity with test formats and targeted practice can help candidates perform more consistently under assessment conditions.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-[#0B1D51] text-white">
              OPC · TOC · Private Assessment Centres
            </Badge>

            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] tracking-tight mb-4">
              Prepare for Official Train Driver Assessments
            </h2>

            <p className="text-[#64748B] max-w-2xl mx-auto">
              Our practice exercises are designed to build the cognitive skills measured in official psychometric batteries 
              used by Occupational Psychology Centres (OPC) and Train Operating Companies across the UK.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {[
              { practice: 'Sustained Attention', official: 'WAFV Vigilance Test', desc: 'Monitoring tasks measuring sustained attention and response to infrequent stimuli', icon: Eye },
              { practice: 'Speed & Distance Judgement', official: 'ATAVT Perception Test', desc: 'Rapid visual processing and scene perception under time pressure', icon: Gauge },
              { practice: 'Dual-Task Attention', official: 'TEA-Occ Divided Attention', desc: 'Simultaneous visual scanning and audio monitoring', icon: SplitSquareVertical },
              { practice: 'Concentration Grid', official: 'Group Bourdon-style Tests', desc: 'Sustained concentration and accuracy in repetitive scanning tasks', icon: Focus },
              { practice: 'Rules & Procedures', official: 'TRP Trainability Test', desc: 'Memorisation and application of procedural rules under time constraints', icon: BookOpen },
              { practice: 'Motor Coordination', official: '2HAND Coordination Test', desc: 'Bilateral hand-eye coordination using independent controls', icon: Hand },
            ].map((item, i) => (
              <motion.div key={i} variants={itemVariants}>
                <Card className="h-full border-slate-200 hover:border-[#0B1D51]/30 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-[#0B1D51]/5 rounded-lg flex items-center justify-center">
                        <item.icon className="w-5 h-5 text-[#0B1D51]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#64748B] uppercase tracking-wide">Our Practice</p>
                        <CardTitle className="font-heading text-lg text-[#0B1D51]">
                          {item.practice}
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div className="bg-[#FF9F1C]/10 rounded-lg p-3 mb-3 border border-[#FF9F1C]/20">
                      <p className="text-xs text-[#FF9F1C] font-semibold uppercase tracking-wide mb-1">
                        Prepares you for
                      </p>
                      <p className="text-sm font-semibold text-[#0B1D51]">
                        {item.official}
                      </p>
                    </div>

                    <p className="text-sm text-[#64748B]">
                      {item.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-10">
            <p className="text-sm text-[#64748B] mb-4">
              Our exercises align with RIS-3751-TOM standards used across UK train driver selection.
            </p>

            <Link to="/tests">
              <Button className="bg-[#0B1D51] hover:bg-[#0B1D51]/90">
                View All Practice Assessments
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-red-100 text-red-700">
                The Reality
              </Badge>
              
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] tracking-tight mb-6">
                How Hard Are Train Driver Psychometric Tests?
              </h2>
              
              <div className="space-y-4 text-[#334155]">
                <p className="text-lg">
                  Train driver psychometric tests are <strong>significantly more challenging</strong> than most candidates expect. With large numbers of applicants competing for limited trainee driver positions, these assessments are designed to identify candidates with the required safety-critical aptitudes.
                </p>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                  <p className="font-semibold text-red-800 mb-2">Why preparation matters:</p>
                  <ul className="space-y-1 text-red-700">
                    <li>• The tests require sustained concentration under time pressure</li>
                    <li>• Candidates must maintain accuracy across repetitive tasks</li>
                    <li>• Some assessments require divided attention and rapid decision-making</li>
                    <li>• Failed formal attempts can restrict future applications</li>
                  </ul>
                </div>
                
                <p>
                  Many candidates fail not because they lack potential, but because they are unfamiliar with the format, pace and mental demand of the assessment environment.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#0B1D51] rounded-xl p-6 sm:p-8 text-white"
            >
              <h3 className="font-heading text-2xl font-bold mb-6">
                Why Candidates Fail
              </h3>

              <ul className="space-y-4">
                {[
                  { icon: Clock, text: "Poor time management under pressure" },
                  { icon: AlertCircle, text: "Unfamiliarity with test formats" },
                  { icon: Target, text: "Inconsistent concentration" },
                  { icon: BarChart3, text: "No practice or benchmark data" },
                  { icon: Brain, text: "Underestimating cognitive demands" },
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#FF9F1C]" />
                    </div>
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-8 pt-6 border-t border-white/20">
                <p className="text-white/80 text-sm mb-4">
                  Prepare before you sit a formal assessment.
                </p>

                <Link to="/register">
                  <Button className="w-full bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white">
                    Start Preparing Today
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#0B1D51]/10 text-[#0B1D51]">
              Comprehensive Coverage
            </Badge>
            
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] tracking-tight mb-4">
              Train Driver Psychometric Test Categories
            </h2>
            
            <p className="text-[#64748B] max-w-2xl mx-auto">
              Our platform covers the main cognitive assessment areas tested during UK train driver recruitment, 
              aligned to RIS-3751-TOM standards.
            </p>
          </div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {testCategories.map((category, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card className="h-full bg-white border-slate-200 hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-[#0B1D51]/5 rounded-lg flex items-center justify-center mb-4">
                      <category.icon className="w-6 h-6 text-[#0B1D51]" />
                    </div>

                    <h3 className="font-heading text-lg font-bold text-[#0B1D51] mb-2">
                      {category.name}
                    </h3>

                    <p className="text-sm text-[#64748B]">
                      {category.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-10">
            <Link to="/tests">
              <Button size="lg" className="bg-[#0B1D51] hover:bg-[#0B1D51]/90">
                Explore All Test Categories
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-4 bg-[#2CB67D]/10 text-[#2CB67D]">
              The Evidence
            </Badge>
            
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] tracking-tight mb-6">
              Why Practice Improves Your Psychometric Test Performance
            </h2>
            
            <div className="text-left prose prose-lg max-w-none text-[#334155]">
              <p>
                Psychometric tests measure relevant underlying abilities, but performance is also affected by familiarity, confidence, pacing, instructions, and test-taking discipline. Practising with structured, interactive exercises helps candidates understand the format and maintain consistency under time pressure.
              </p>
            </div>
          </div>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8 mt-12"
          >
            {[
              {
                stat: "Focus",
                label: "Sustained concentration",
                description: "Build consistency across repetitive and time-pressured tasks"
              },
              {
                stat: "Speed",
                label: "Controlled response pace",
                description: "Practise responding quickly without losing accuracy"
              },
              {
                stat: "Insight",
                label: "Identify weak areas",
                description: "Use practice performance to target the areas needing most work"
              }
            ].map((item, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="text-4xl font-bold text-[#2CB67D] mb-2">
                  {item.stat}
                </div>
                <div className="font-semibold text-[#0B1D51] mb-2">
                  {item.label}
                </div>
                <p className="text-sm text-[#64748B]">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#0B1D51] overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-[#FF9F1C] text-white">
                Industry Expertise
              </Badge>
              
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
  Why Choose TDF Psychometrics for Your Preparation?
</h2>

<div className="space-y-6 text-white/85">
  <p className="text-lg leading-relaxed">
    TDF Psychometrics is developed by the <strong className="text-white">Train Driver Foundation</strong>, 
    a specialist UK train driver recruitment preparation provider. The platform is built around the 
    cognitive, psychomotor and behavioural aptitudes used in train driver selection, rather than generic 
    online brain-training exercises.
  </p>

  <p className="text-base leading-relaxed text-white/75">
    RIS-3751-TOM sets out the standardised train driver psychometric assessment process for GB mainline 
    driver selection. Our practice environment helps candidates build familiarity with the types of 
    skill demands reflected in that process: attention, vigilance, memory, reasoning, perception, 
    reaction time, hand coordination, communication and behavioural judgement.
  </p>

  <div className="grid sm:grid-cols-2 gap-3">
    {[
      "Built around train driver selection criteria",
      "Aligned to RIS-3751-TOM skill areas",
      "Relevant to OPC and TOC assessment preparation",
      "Rail-focused practice, not generic puzzles",
      "Progress tracking to identify weaker areas",
      "Progressive difficulty to build consistency",
      "Mobile-optimised for regular short practice",
      "Designed for realistic time-pressure practice"
    ].map((item, index) => (
      <div
        key={index}
        className="flex items-start gap-3 rounded-lg bg-white/10 border border-white/10 p-3"
      >
        <CheckCircle className="w-5 h-5 text-[#2CB67D] flex-shrink-0 mt-0.5" />
        <span className="text-sm font-medium text-white/90 leading-snug">
          {item}
        </span>
      </div>
    ))}
  </div>

  <div className="rounded-xl bg-[#FF9F1C]/10 border border-[#FF9F1C]/30 p-5">
    <p className="text-sm sm:text-base leading-relaxed text-white/85">
      The aim is not to promise an assessment pass. The aim is to help candidates practise the 
      relevant mental skills, understand the format of demanding selection-style tasks, and attend 
      formal assessment with better familiarity, pacing and confidence.
    </p>
  </div>
</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-xl p-6 sm:p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#0B1D51] rounded-lg flex items-center justify-center">
                    <Award className="w-8 h-8 text-[#FF9F1C]" />
                  </div>

                  <div>
  <h3 className="font-heading text-xl font-bold text-[#0B1D51]">
    Industry Relevant
  </h3>
  <p className="text-[#64748B]">
    Built around the core train driver selection criteria in RIS-3751-TOM.
  </p>
</div>
</div>

<div className="space-y-5">
  <div className="rounded-lg bg-[#F8FAFC] border border-slate-200 p-4">
    <p className="text-xs uppercase tracking-wide font-semibold text-[#64748B] mb-2">
      RIS-3751-TOM selection criteria
    </p>

    <div className="grid grid-cols-2 gap-2 text-sm">
      {[
        "Attention",
        "Vigilance",
        "Memory",
        "Reasoning",
        "Perception",
        "Reaction time",
        "Hand coordination",
        "Communication",
        "Behavioural aptitudes",
      ].map((item) => (
        <div key={item} className="flex items-center gap-2 text-[#0B1D51]">
          <CheckCircle className="w-4 h-4 text-[#2CB67D] flex-shrink-0" />
          <span className="font-medium">{item}</span>
        </div>
      ))}
    </div>
  </div>

  <div className="flex justify-between items-center py-3 border-b border-slate-100">
    <span className="text-[#64748B]">Candidates Prepared</span>
    <span className="font-bold text-[#0B1D51]">1,000+</span>
  </div>

  <div className="flex justify-between items-center py-3 border-b border-slate-100">
    <span className="text-[#64748B]">Selection Criteria Covered</span>
    <span className="font-bold text-[#0B1D51]">9</span>
  </div>

  <div className="flex justify-between items-center py-3 border-b border-slate-100">
    <span className="text-[#64748B]">Practice Difficulty</span>
    <span className="font-bold text-[#0B1D51]">Progressive levels</span>
  </div>

  <div className="rounded-lg bg-[#0B1D51]/5 border border-[#0B1D51]/10 p-4">
    <p className="text-sm text-[#334155] leading-relaxed mb-3">
      RIS-3751-TOM identifies the core cognitive, psychomotor and behavioural aptitudes used in train driver selection, including attention, vigilance, memory, reasoning, perception, reaction time, hand coordination, communication and behavioural aptitudes.
    </p>

    <Link
      to="/skills"
      className="inline-flex items-center text-sm font-semibold text-[#0B1D51] hover:underline"
    >
      View the train driver skills we help you build
      <ArrowRight className="ml-2 w-4 h-4" />
    </Link>
  </div>
</div>
                
                <Link to="/register" className="block mt-6">
                  <Button className="w-full bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white" size="lg">
                    Create Free Account
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#0B1D51]/10 text-[#0B1D51]">
              Simple Process
            </Badge>
            
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] tracking-tight mb-4">
              How to Prepare for Train Driver Psychometric Tests
            </h2>
            
            <p className="text-[#64748B] max-w-2xl mx-auto">
              Our structured approach helps you build skills progressively and identify areas needing improvement.
            </p>
          </div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-8"
          >
            {[
              {
                step: "1",
                title: "Create Account",
                description: "Sign up free and get practice attempts to explore the platform",
                icon: Users
              },
              {
                step: "2",
                title: "Practice Tests",
                description: "Work through all assessment categories at your own pace",
                icon: Target
              },
              {
                step: "3",
                title: "Track Progress",
                description: "Monitor your performance and identify weak areas to focus on",
                icon: BarChart3
              },
              {
                step: "4",
                title: "Attend Prepared",
                description: "Approach your assessment with greater familiarity and confidence",
                icon: Trophy
              }
            ].map((item, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="relative inline-block mb-4">
                  <div className="w-16 h-16 bg-[#0B1D51] rounded-full flex items-center justify-center mx-auto">
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#FF9F1C] rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {item.step}
                  </div>
                </div>

                <h3 className="font-heading text-lg font-bold text-[#0B1D51] mb-2">
                  {item.title}
                </h3>

                <p className="text-sm text-[#64748B]">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#0B1D51]/10 text-[#0B1D51]">
              <HelpCircle className="w-3 h-3 mr-1" />
              Common Questions
            </Badge>
            
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] tracking-tight mb-4">
              Frequently Asked Questions About Train Driver Psychometric Tests
            </h2>
            
            <p className="text-[#64748B]">
              Everything you need to know about UK train driver assessments and preparation.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className="bg-white rounded-lg border border-slate-200 px-6"
              >
                <AccordionTrigger className="text-left font-heading !text-lg sm:!text-xl font-semibold text-[#0B1D51] hover:no-underline py-5 leading-snug [&>svg]:w-5 [&>svg]:h-5">
                  {faq.question}
                </AccordionTrigger>

                <AccordionContent className="!text-base sm:!text-lg text-[#334155] pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          
          <div className="text-center mt-10">
            <p className="text-[#64748B] mb-4">Still have questions?</p>
            <Link to="/contact">
              <Button variant="outline" className="border-[#0B1D51] text-[#0B1D51]">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0B1D51]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
              Don't Risk Your Train Driver Career
            </h2>
            
            <p className="text-white/80 mb-4 max-w-2xl mx-auto">
              With limited formal assessment opportunities and high competition, preparation matters. 
              Use TDF Psychometrics to build familiarity before you sit the real assessment.
            </p>
            
            <p className="text-[#FF9F1C] font-semibold mb-8">
              Start with free practice attempts — no payment required.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white"
                  data-testid="cta-final-btn"
                >
                  Create Free Account
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>

              <Link to="/tests">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10"
                >
                  Explore All Tests
                </Button>
              </Link>
            </div>
            
            <p className="text-white/60 text-sm mt-8">
              No credit card required • Instant access • Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
};

export default HomePage;